import React from "react";
import { Box } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { dt } from "../../../../shared/themes/redesignTokens";
import type { FontTheme } from "../../../../shared/themes/fontThemes";

const FONT_DESCRIPTIONS: Record<string, string> = {
  system: "Segoe UI / SF Pro + Consolas / SF Mono",
  inter: "Inter + IBM Plex Mono",
  "space-grotesk": "Space Grotesk + Fira Code",
  "space-grotesk-geist": "Space Grotesk + Geist Mono",
};

interface FontThemePreviewCardProps {
  theme: FontTheme;
  isActive: boolean;
  onClick: () => void;
  translate: (key: string) => string;
}

const FontThemePreviewCard: React.FC<FontThemePreviewCardProps> = ({
  theme,
  isActive,
  onClick,
  translate,
}) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        position: "relative",
        border: `2px solid ${isActive ? dt.blue : dt.cardBorder}`,
        borderRadius: dt.radiusSm,
        padding: "16px",
        cursor: "pointer",
        backgroundColor: isActive ? dt.blueSoft : dt.cardBg,
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: isActive ? dt.blue : dt.textMuted,
          boxShadow: dt.cardShadowHover,
        },
      }}
    >
      {isActive && (
        <CheckCircleIcon
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            color: dt.blue,
            fontSize: 22,
          }}
        />
      )}

      {/* Font name */}
      <Box
        sx={{
          fontFamily: dt.font,
          fontSize: "14px",
          fontWeight: 700,
          color: dt.textPrimary,
          mb: 0.5,
        }}
      >
        {translate(theme.labelKey.replace("settings:", ""))}
      </Box>
      <Box
        sx={{
          fontFamily: dt.font,
          fontSize: "12px",
          color: dt.textSecondary,
          mb: 1.5,
        }}
      >
        {FONT_DESCRIPTIONS[theme.id] || ""}
      </Box>

      {/* Mini UI mock — all text in the theme's actual fonts */}
      <Box
        sx={{
          backgroundColor: "#F8F9FA",
          border: "1px solid #E5E7EB",
          borderRadius: "6px",
          padding: "10px 12px",
          minHeight: 100,
        }}
      >
        {/* Nav pills row */}
        <Box sx={{ display: "flex", gap: "6px", mb: "10px" }}>
          {["Dashboard", "Machines", "Offers"].map((label, i) => (
            <Box
              key={label}
              sx={{
                fontFamily: theme.sans,
                fontSize: "10px",
                fontWeight: i === 0 ? 600 : 400,
                color: i === 0 ? "#FFFFFF" : dt.textSecondary,
                backgroundColor: i === 0 ? dt.blue : "#EBEDF0",
                borderRadius: "10px",
                px: "8px",
                py: "3px",
                lineHeight: 1.3,
              }}
            >
              {label}
            </Box>
          ))}
        </Box>

        {/* Stat value in mono */}
        <Box
          sx={{
            fontFamily: theme.mono,
            fontSize: "22px",
            fontWeight: 700,
            color: dt.textPrimary,
            lineHeight: 1.2,
          }}
        >
          94.2%
        </Box>

        {/* Label under stat */}
        <Box
          sx={{
            fontFamily: theme.sans,
            fontSize: "11px",
            fontWeight: 500,
            color: dt.textSecondary,
            mt: "2px",
            mb: "8px",
          }}
        >
          Uptime
        </Box>

        {/* Small action button */}
        <Box
          sx={{
            display: "inline-block",
            fontFamily: theme.sans,
            fontSize: "10px",
            fontWeight: 600,
            color: "#FFFFFF",
            backgroundColor: dt.blue,
            borderRadius: "4px",
            px: "10px",
            py: "4px",
            lineHeight: 1.3,
          }}
        >
          View Details
        </Box>
      </Box>
    </Box>
  );
};

export default FontThemePreviewCard;
